import React from 'react';
import { HashRouter, Route, Routes, useLocation } from 'react-router-dom';

/**
 * MUI Components
 */
import { Container } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

/**
 * App Components
 */
import ConnectionStatus from './components/ConnectionStatus';
import Login from './components/Login/Login';
import Profile from './pages/Profile';
import MyWorks from './pages/Works/MyWorks';
import WorkReportPage from './pages/Works/WorkReportPage';
import VehicleAlerts from './pages/Vehicles/VehicleAlerts';
import Notifications from './pages/Notifications/Notifications';
import BottomNav from './components/BottomNav';
import ResponsiveAppBar from './components/ResponsiveAppBar';
import Version from './components/Version';

/**
 * Contexts
 */
import { AppContext } from './contexts/AppContext';
import { AppMenuProvider } from './contexts/NavBarContext';
import { NotificationProvider } from './contexts/NotificationContext';

/**
 * Hooks
 */
import { useTranslation } from 'react-i18next';
import useToken from './hooks/useToken';
import useUserInfo from './hooks/User/useUserInfo';
import MyReportsPage from './pages/Works/MyReportsPage';

/**
 * Styles
 */
const styles = {
    noselect: {
        userSelect: 'none'
    }
};

function App() {
    // Set translation hook
    const { t } = useTranslation();

    /**
     * Handle User Login
     * Refs: https://www.digitalocean.com/community/tutorials/how-to-add-login-authentication-to-react-applications
     */
    const { token, setToken } = useToken();
    const { userInfo, loading, error } = useUserInfo(token);

    // Login user?
    if (!token) {

        // FIXME: Only for dev testing!
        const credentials = {
            username: 'rafael.garcia@geoinnova.org',
            password: '1234567890'
        };

        return (
            <Container component="main" maxWidth="xs">
                {/* <Login setToken={setToken} credentials={credentials} /> */}
                <Login setToken={setToken} />
                <Version />
            </Container>
        );
    }

    if (loading) {
        return (
            <div>
                {t('loading')}...
                <CircularProgress />
            </div>
        );
    }

    if (!userInfo || userInfo === null || error) {
        return <div>{t('error')}</div>;
    }

    // Populate app context with data
    const appData = { token, userInfo };

    const MainContent = () => {
        const location = useLocation();
        const hideBottomNavPaths = [
            '/work-report',
            '/work-report/edit',
            '/work-report/edit/local'
        ];

        return (
            <>
                <Routes>
                    <Route path="/" element={<MyWorks />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/work-report" element={<WorkReportPage />} />
                    <Route path="/work-report/edit/local/:key" element={<WorkReportPage mode='edit' />} />
                    <Route path="/work-report/edit/:id" element={<WorkReportPage mode='edit' />} />
                    <Route path="/my-reports" element={<MyReportsPage />} />
                    <Route path="/vehicles/alerts" element={<VehicleAlerts />} />
                </Routes>
                {!hideBottomNavPaths.some(path => location.pathname.startsWith(path)) && <BottomNav />}
            </>
        );
    };

    return (
        <>
            <ConnectionStatus>
                <NotificationProvider>
                    <AppContext.Provider value={appData}>
                        <div style={styles.noselect} className="wrapper">
                            <AppMenuProvider>
                                <HashRouter>
                                    <ResponsiveAppBar userInfo={userInfo} />
                                    <MainContent />
                                </HashRouter>
                            </AppMenuProvider>
                        </div>
                    </AppContext.Provider>
                </NotificationProvider>
            </ConnectionStatus >
        </>
    );
}

export default App;
